import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface MealOption {
  id: string
  name: string
  emoji: string
  description?: string
  recipe?: string
  meal_type: string
  category: string
  prep_time_minutes?: number
  cost_estimate?: number
}

// Query keys factory
export const mealOptionsKeys = {
  all: ['mealOptions'] as const,
  list: () => [...mealOptionsKeys.all, 'list'] as const,
}

/**
 * Fetch meal options
 */
export function useMealOptions() {
  return useQuery({
    queryKey: mealOptionsKeys.list(),
    queryFn: async () => {
      const data = await apiClient.getMealOptions()
      return (data || []) as MealOption[]
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Update a meal option
 */
export function useUpdateMealOption() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { mealOptionId: string; updates: Partial<MealOption> }) => {
      const { mealOptionId, updates } = params
      return await apiClient.updateMealOption(mealOptionId, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealOptionsKeys.all })
    },
  })
}

/**
 * Delete a meal option
 */
export function useDeleteMealOption() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (mealOptionId: string) => {
      return await apiClient.deleteMealOption(mealOptionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealOptionsKeys.all })
    },
  })
}

/**
 * Create a meal option
 */
export function useCreateMealOption() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (mealOption: Partial<MealOption>) => {
      return await apiClient.createMealOption(mealOption)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealOptionsKeys.all })
    },
  })
}
